import { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import type { ActiveClinic } from '@/types/api';
import { clinicPickerHref, TENANT, tenantHref } from '@/routing/tenant';

/**
 * Where a just-authenticated user goes, and how to get them there.
 *
 * "How" matters as much as "where" because each clinic is its own URL namespace
 * and the router's `basename` is fixed at mount. A destination in a DIFFERENT
 * namespace than the current page cannot be reached with `navigate()` — the
 * router would resolve it under the wrong prefix — so it has to be a full page
 * load. Callers must honour the `kind` rather than assuming one.
 */
export type PostAuthDestination =
  | { kind: 'router'; to: string }
  | { kind: 'hard'; href: string };

/**
 * Decide where to send a user who has just signed in or registered.
 *
 * - Already inside a clinic they belong to → stay put, honouring the page they
 *   were originally trying to reach.
 * - Exactly one clinic → straight into it. No choice to offer.
 * - Anything else — no clinics, or several → the picker. Zero and many are the
 *   same question ("which clinic?"), and `/clinics` is the only place that knows
 *   about both memberships AND pending invitations, so it is the only place that
 *   can tell "you have nowhere to go yet, let's create one" apart from "you have
 *   an invitation waiting".
 *
 * Both shortcuts consider only ENTERABLE clinics. A disabled one cannot be
 * entered — `RequireAuth` bounces straight back out — so treating it as somewhere
 * to go produces an infinite loop for anyone whose only clinic was switched off:
 * shortcut in, bounced to the picker, shortcut in again. The picker itself still
 * sees the full list, because it is where a disabled clinic must remain visible.
 *
 * But filtering alone is not enough, and the difference matters: a member of two
 * clinics, one of them disabled, would filter down to one and get shortcut into
 * the survivor — never seeing the picker, and so never seeing the badge that says
 * why their other clinic stopped answering. Their bookmark for it would silently
 * open the OTHER clinic instead, forever, with no explanation anywhere. So the
 * shortcut requires that nothing is being hidden: any disabled membership sends
 * them to the picker, which is the one screen built to explain it.
 *
 * `requestedFrom` is a basename-relative path captured by `RequireAuth`, so it
 * composes with either namespace unchanged.
 */
export function pickPostAuthDestination(
  memberships: ActiveClinic[],
  requestedFrom?: string,
): PostAuthDestination {
  const target = requestedFrom ?? '/';
  const enterable = memberships.filter((m) => !m.disabled);

  if (TENANT.mode === 'tenant' && enterable.some((m) => m.slug === TENANT.slug)) {
    return { kind: 'router', to: target };
  }

  const only =
    memberships.length === 1 && enterable.length === 1 ? enterable[0] : undefined;
  if (only) return { kind: 'hard', href: tenantHref(only.slug, target) };

  // The picker is global. Reaching it from inside a clinic's namespace crosses
  // the basename, so that case has to be a hard navigation.
  const href = clinicPickerHref(requestedFrom);
  return TENANT.mode === 'global' ? { kind: 'router', to: href } : { kind: 'hard', href };
}

/** Perform a `PostAuthDestination`, picking the right navigation mechanism. */
export function useGoToPostAuthRoute(): (
  memberships: ActiveClinic[],
  requestedFrom?: string,
) => void {
  const navigate = useNavigate();
  return useCallback(
    (memberships, requestedFrom) => {
      const dest = pickPostAuthDestination(memberships, requestedFrom);
      if (dest.kind === 'router') navigate(dest.to, { replace: true });
      else window.location.replace(dest.href);
    },
    [navigate],
  );
}
